import React from 'react';

const EducaToroTerms: React.FC = () => {
  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px', fontFamily: 'Arial, sans-serif', lineHeight: '1.6' }}>
      <h1 style={{ borderBottom: '2px solid #333', paddingBottom: '10px' }}>EducaToro SMS Terms & Conditions</h1>
      <p><em>Last Updated: February 5, 2026</em></p>

      <h2>1. Acceptance of Terms</h2>
      <p>
        By providing your mobile phone number to receive SMS notifications from EducaToro, you agree to these Terms & Conditions. If you do not agree, do not provide your phone number or reply STOP to opt out immediately.
      </p>

      <h2>2. Service Description</h2>
      <p>
        EducaToro provides SMS text message notifications to parents about their child's educational activities. These notifications include:
      </p>
      <ul>
        <li>Attendance alerts (absent, present, tardy notifications)</li>
        <li>Grade updates and exam results</li>
        <li>Important academic communications from teachers</li>
        <li>Exam and assignment reminders</li>
      </ul>
      <p>
        <strong>This is a transactional notification service only.</strong> We do not send marketing, promotional, or advertising messages.
      </p>

      <h2>3. Enrollment and Consent</h2>
      <p>
        You are enrolled in EducaToro SMS notifications when:
      </p>
      <ul>
        <li>Your child's teacher enrolls your student in EducaToro</li>
        <li>You provide your mobile phone number to the teacher for school communications</li>
        <li>You have not opted out by replying STOP</li>
      </ul>
      <p>
        By providing your phone number, you expressly consent to receive automated SMS messages from EducaToro. Consent is not required as a condition of enrollment or use of EducaToro's other services.
      </p>

      <h2>4. Message Frequency</h2>
      <p>
        Message frequency varies and depends on:
      </p>
      <ul>
        <li>Your child's attendance and academic activity</li>
        <li>Teacher communication frequency</li>
        <li>School calendar and exam schedules</li>
      </ul>
      <p>
        You may receive anywhere from 1-10 messages per week during active school periods. There is no maximum limit as notifications are event-driven based on your child's educational activities.
      </p>

      <h2>5. Costs and Carrier Charges</h2>
      <p>
        EducaToro does not charge for SMS notifications sent to parents during the free tier (offline-only use). Standard message and data rates from your mobile carrier may apply. Contact your mobile carrier for details about your messaging plan.
      </p>
      <p>
        <strong>Paid Subscription:</strong> If your teacher subscribes to EducaToro's paid plan ($10/month with 20 SMS credits), there is no additional cost to you as a parent. Your teacher's subscription covers the SMS delivery costs.
      </p>

      <h2>6. Opt-Out Rights</h2>
      <p>
        You may opt out of EducaToro SMS notifications at any time by:
      </p>
      <ul>
        <li>Replying <strong>STOP</strong> to any message from EducaToro</li>
        <li>Replying <strong>UNSUBSCRIBE</strong> to any message</li>
        <li>Contacting us directly at landaverde.erik10@gmail.com</li>
      </ul>
      <p>
        After opting out, you will receive one final confirmation message, and no further messages will be sent. To re-subscribe, contact your child's teacher to re-enroll.
      </p>

      <h2>7. Help and Customer Support</h2>
      <p>
        For help or questions about SMS notifications:
      </p>
      <ul>
        <li>Reply <strong>HELP</strong> to any message</li>
        <li>Email: landaverde.erik10@gmail.com</li>
        <li>Contact your child's teacher directly</li>
      </ul>

      <h2>8. Supported Carriers</h2>
      <p>
        EducaToro SMS notifications are supported by major carriers including AT&T, T-Mobile, Verizon, Sprint, and others. Not all carriers may be supported. If you experience delivery issues, contact us at support@educatoro.com.
      </p>

      <h2>9. Accuracy of Information</h2>
      <p>
        While we strive for accuracy, EducaToro is not responsible for:
      </p>
      <ul>
        <li>Errors in attendance or grade data entered by teachers</li>
        <li>Delayed message delivery due to carrier issues</li>
        <li>Messages not received due to network outages or device issues</li>
      </ul>
      <p>
        SMS notifications are informational and should be verified with your child's teacher or school for official records.
      </p>

      <h2>10. Privacy and Data Use</h2>
      <p>
        Your privacy is important to us. Please review our <a href="/educatoro-privacy" style={{ color: '#0066cc' }}>Privacy Policy</a> for details on how we collect, use, and protect your phone number and message data.
      </p>

      <h2>11. Service Availability</h2>
      <p>
        We make reasonable efforts to ensure service availability, but we do not guarantee:
      </p>
      <ul>
        <li>Uninterrupted or error-free service</li>
        <li>Delivery of every message due to carrier limitations</li>
        <li>Specific response times for support requests</li>
      </ul>
      <p>
        EducaToro reserves the right to modify, suspend, or discontinue SMS services at any time with reasonable notice.
      </p>

      <h2>12. Prohibited Uses</h2>
      <p>
        You may not:
      </p>
      <ul>
        <li>Provide false or fraudulent phone numbers</li>
        <li>Attempt to circumvent opt-out mechanisms</li>
        <li>Use EducaToro SMS for any unlawful purpose</li>
        <li>Share or forward messages in violation of student privacy laws</li>
      </ul>

      <h2>13. Limitation of Liability</h2>
      <p>
        To the fullest extent permitted by law, EducaToro and its operators are not liable for:
      </p>
      <ul>
        <li>Costs incurred from carrier charges</li>
        <li>Damages from missed or delayed notifications</li>
        <li>Any indirect, incidental, or consequential damages</li>
      </ul>
      <p>
        Our maximum liability is limited to the amount paid by your child's teacher for the EducaToro subscription (if applicable).
      </p>

      <h2>14. Compliance with Laws</h2>
      <p>
        EducaToro SMS services comply with:
      </p>
      <ul>
        <li>Telephone Consumer Protection Act (TCPA)</li>
        <li>CAN-SPAM Act</li>
        <li>Cellular Telecommunications Industry Association (CTIA) guidelines</li>
        <li>Family Educational Rights and Privacy Act (FERPA) for student data</li>
      </ul>

      <h2>15. International Use</h2>
      <p>
        EducaToro is primarily designed for use in the United States and Latin America (particularly El Salvador). International messaging rates may apply if used outside these regions.
      </p>

      <h2>16. Changes to Terms</h2>
      <p>
        We may update these Terms & Conditions from time to time. Changes will be posted at this URL with an updated "Last Updated" date. Continued receipt of SMS messages after changes constitutes acceptance of the updated terms.
      </p>

      <h2>17. Governing Law</h2>
      <p>
        These Terms are governed by the laws of the State of California, United States, without regard to conflict of law provisions.
      </p>

      <h2>18. Severability</h2>
      <p>
        If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
      </p>

      <h2>19. Contact Information</h2>
      <p>
        For questions about these Terms & Conditions:
      </p>
      <p>
        <strong>Email:</strong> landaverde.erik10@gmail.com<br/>
        <strong>SMS Help:</strong> Reply HELP to any message<br/>
        <strong>Mailing Address:</strong> 22555 Nadine Cir, Torrance, CA 90505
      </p>

      <h2>20. Acknowledgment</h2>
      <p>
        By providing your phone number and receiving SMS notifications from EducaToro, you acknowledge that you have read, understood, and agree to be bound by these Terms & Conditions and our Privacy Policy.
      </p>
    </div>
  );
};

export default EducaToroTerms;